import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Alert,
  List,
  ListItem,
  ListItemText
} from '@mui/material';
import { useLocale } from '../context/LocaleContext';
import LanguageSwitcher from './LanguageSwitcher';

const LocaleDemo = () => {
  const { 
    t, 
    currentLocale, 
    setCurrentLocale, 
    formatNumber, 
    formatCurrency, 
    formatDate, 
    isRTL,
    isInitialized,
    i18n 
  } = useLocale();
  
  const [testNumber] = useState(1234.56);
  const [testAmount] = useState(99.99);
  const [testDate] = useState(new Date());

  if (!isInitialized) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h6">Loading locale system...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, direction: isRTL ? 'rtl' : 'ltr' }}>
      <Typography variant="h4" gutterBottom>
        Locale System Demo
      </Typography>
      
      <Alert severity="info" sx={{ mb: 3 }}>
        This page demonstrates the internationalization (i18n) system. 
        Current locale: <strong>{currentLocale}</strong> | 
        Direction: <strong>{isRTL ? 'RTL' : 'LTR'}</strong> | 
        i18next ready: <strong>{i18n.isInitialized ? 'Yes' : 'No'}</strong>
      </Alert>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Language Selection
              </Typography>
              <FormControl fullWidth>
                <InputLabel>Language</InputLabel>
                <Select
                  value={currentLocale}
                  label="Language"
                  onChange={(e) => setCurrentLocale(e.target.value)}
                >
                  <MenuItem value="en">English</MenuItem>
                  <MenuItem value="ar">العربية (Arabic)</MenuItem>
                </Select>
              </FormControl>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Translation Test
              </Typography>
              <Typography variant="body1">
                <strong>Dashboard:</strong> {t('navigation.dashboard')}
              </Typography>
              <Typography variant="body1">
                <strong>Patients:</strong> {t('navigation.patients')}
              </Typography>
              <Typography variant="body1">
                <strong>Appointments:</strong> {t('navigation.appointments')}
              </Typography>
              <Typography variant="body1">
                <strong>Billing:</strong> {t('navigation.billing')}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Number Formatting
              </Typography>
              <Typography variant="body1">
                <strong>Number:</strong> {formatNumber(testNumber)}
              </Typography>
              <Typography variant="body1">
                <strong>Currency:</strong> {formatCurrency(testAmount)}
              </Typography>
              <Typography variant="body1">
                <strong>Date:</strong> {formatDate(testDate)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                System Status
              </Typography>
              <Typography variant="body1">
                <strong>i18next Initialized:</strong> {i18n.isInitialized ? '✅ Yes' : '❌ No'}
              </Typography>
              <Typography variant="body1">
                <strong>Current Language:</strong> {i18n.language}
              </Typography>
              <Typography variant="body1">
                <strong>Available Languages:</strong> {i18n.languages?.join(', ')}
              </Typography>
              <Typography variant="body1">
                <strong>Direction:</strong> {isRTL ? 'Right-to-Left' : 'Left-to-Right'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Divider sx={{ my: 3 }} />
      
      <Typography variant="h6" gutterBottom>
        Test Translation Keys
      </Typography>
      <Grid container spacing={2}>
        {[
          'navigation.dashboard',
          'navigation.patients', 
          'navigation.appointments',
          'navigation.billing',
          'navigation.facilities',
          'navigation.encounters',
          'navigation.prescriptions',
          'navigation.pharmacy',
          'navigation.messaging',
          'navigation.care_coordination'
        ].map((key) => (
          <Grid item xs={12} sm={6} md={4} key={key}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="caption" color="textSecondary">
                  {key}
                </Typography>
                <Typography variant="body2">
                  {t(key)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default LocaleDemo; 